import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Send, CheckCircle2, RotateCcw, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmationDialog } from '../../components/ui/confirmation-dialog';
import { WorkerAckDot } from './WorkerAckDot';

/**
 * Job-level acknowledgment panel.
 *
 * Shows every allocation + worker-allocation on the job with status, timestamps,
 * and per-row actions (Send / Re-send / Mark confirmed). Includes a bulk
 * "Send all pending" button.
 *
 * Auto-hidden when the `worker_job_confirmation` feature flag is off.
 */
// `date` (YYYY-MM-DD) limits the panel to rows covering that day — single-day view of a multi-day job
export const JobAckPanel = ({ jobId, onChange, date = null }) => {
  const { api } = useAuth();
  const { isEnabled } = useFeatureFlags();
  const enabled = isEnabled('worker_job_confirmation');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyRow, setBusyRow] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', description: '', onConfirm: null });
  const [bulkBusy, setBulkBusy] = useState(false);
  const [cleanupBusy, setCleanupBusy] = useState(false);

  const fetchData = useCallback(async () => {
    if (!jobId || !enabled) { setLoading(false); return; }
    try {
      const res = await api.get(`/jobs/${jobId}/acknowledgements`, { params: date ? { date } : undefined });
      setData(res.data);
    } catch (err) {
      if (err.response?.status !== 404) {
        toast.error('Failed to load acknowledgements', { description: err.response?.data?.detail || err.message });
      }
    } finally {
      setLoading(false);
    }
  }, [api, jobId, enabled, date]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!enabled) return null;
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading worker confirmations…
      </div>
    );
  }
  if (!data) return null;

  const allRows = [
    ...(data.allocations || []).map((a) => ({ ...a, _kind: 'allocation' })),
    ...(data.worker_allocations || []).map((a) => ({ ...a, _kind: 'worker_allocation' })),
  ];
  if (!allRows.length) return null;

  const sendRow = async (row) => {
    setBusyRow(row.id);
    try {
      // NOTE: api.baseURL already ends with /api — do NOT prepend it here.
      const path = row._kind === 'allocation'
        ? `/allocations/${row.id}/send-to-worker`
        : `/worker-allocations/${row.id}/send-to-worker`;
      await api.post(path);
      toast.success(`Sent to ${row.operator_name || 'worker'}`);
      await fetchData();
      onChange?.();
    } catch (err) {
      toast.error('Send failed', { description: err.response?.data?.detail || err.message });
    } finally {
      setBusyRow(null);
    }
  };

  const reSendRow = async (row) => {
    if (row._kind !== 'allocation') return sendRow(row);
    setBusyRow(row.id);
    try {
      await api.post(`/allocations/${row.id}/re-send`);
      toast.success('Re-sent');
      await fetchData();
    } catch (err) {
      toast.error('Re-send failed', { description: err.response?.data?.detail || err.message });
    } finally {
      setBusyRow(null);
    }
  };

  const adminConfirmRow = async (row) => {
    setConfirmDialog({
      open: true,
      title: 'Confirm Allocation',
      description: `Mark ${row.operator_name || 'worker'}'s allocation as confirmed? (use when the worker confirmed out of band)`,
      onConfirm: async () => {
        setConfirmDialog(d => ({ ...d, open: false }));
        setBusyRow(row.id);
        try {
          const path = row._kind === 'allocation'
            ? `/allocations/${row.id}/admin-confirm`
            : `/worker-allocations/${row.id}/admin-confirm`;
          await api.post(path, { reason: 'Confirmed by admin from Job Detail panel' });
          toast.success('Marked as confirmed');
          await fetchData();
          onChange?.();
        } catch (err) {
          toast.error('Admin-confirm failed', { description: err.response?.data?.detail || err.message });
        } finally {
          setBusyRow(null);
        }
      },
    });
  };

  const bulkSend = async () => {
    setBulkBusy(true);
    try {
      const res = await api.post(`/jobs/${jobId}/send-to-workers`, null, { params: date ? { date } : undefined });
      const { sent = [], skipped = [] } = res.data || {};
      toast.success(`Sent to ${sent.length} worker${sent.length === 1 ? '' : 's'}${skipped.length ? ` · ${skipped.length} skipped` : ''}`);
      await fetchData();
      onChange?.();
    } catch (err) {
      toast.error('Bulk send failed', { description: err.response?.data?.detail || err.message });
    } finally {
      setBulkBusy(false);
    }
  };

  const summary = data.summary || {};
  const hasDraft = summary.draft > 0;
  const hasExcessiveRows = allRows.length > 50;

  const cleanupOrphans = async () => {
    setCleanupBusy(true);
    try {
      const res = await api.post(`/worker-allocations/job/${jobId}/cleanup-orphans`);
      const { removed = 0, remaining = 0 } = res.data || {};
      if (removed > 0) {
        toast.success(`Cleaned up ${removed} duplicate/orphaned records (${remaining} remaining)`);
        await fetchData();
        onChange?.();
      } else {
        toast.info('No orphaned records found');
      }
    } catch (err) {
      toast.error('Cleanup failed', { description: err.response?.data?.detail || err.message });
    } finally {
      setCleanupBusy(false);
    }
  };

  return (
    <>
    <div className="space-y-3" data-testid="job-ack-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-[#ffd108]" />
          <span className="text-sm font-semibold">Worker confirmations</span>
          <Badge variant="outline" className="text-xs">
            {summary.confirmed || 0} / {summary.total || 0} confirmed
          </Badge>
          {summary.sent > 0 && (
            <Badge variant="outline" className="text-xs border-yellow-500 text-yellow-400">
              {summary.sent} awaiting ack
            </Badge>
          )}
          {summary.requires_re_ack > 0 && (
            <Badge variant="outline" className="text-xs border-amber-500 text-amber-400">
              {summary.requires_re_ack} re-ack required
            </Badge>
          )}
        </div>
        {hasDraft && (
          <Button
            size="sm"
            onClick={bulkSend}
            disabled={bulkBusy}
            className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
            data-testid="bulk-send-to-workers-btn"
          >
            {bulkBusy ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <Send className="mr-2 h-3 w-3" />}
            Send all pending ({summary.draft})
          </Button>
        )}
        {hasExcessiveRows && (
          <Button
            size="sm"
            variant="outline"
            onClick={cleanupOrphans}
            disabled={cleanupBusy}
            className="border-amber-500 text-amber-400 hover:bg-amber-500/10"
            data-testid="cleanup-orphaned-workers-btn"
          >
            {cleanupBusy ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <RotateCcw className="mr-2 h-3 w-3" />}
            Clean up duplicates ({allRows.length})
          </Button>
        )}
      </div>

      <div className="rounded border border-border/50 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/30">
            <tr>
              <th className="text-left px-2 py-1.5 font-medium text-xs text-muted-foreground">Worker</th>
              <th className="text-left px-2 py-1.5 font-medium text-xs text-muted-foreground">Status</th>
              <th className="text-left px-2 py-1.5 font-medium text-xs text-muted-foreground">Notes</th>
              <th className="text-right px-2 py-1.5 font-medium text-xs text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {allRows.map((row) => (
              <tr key={`${row._kind}-${row.id}`} className="border-t border-border/30" data-testid={`ack-row-${row.id}`}>
                <td className="px-2 py-1.5">
                  <div className="flex items-center gap-2">
                    <WorkerAckDot allocation={row} />
                    <span>{row.operator_name || '—'}</span>
                    {row._kind === 'worker_allocation' && (
                      <Badge variant="outline" className="text-[10px]">2nd</Badge>
                    )}
                  </div>
                </td>
                <td className="px-2 py-1.5 text-xs">
                  {row.requires_re_ack ? 'Re-ack required' : (row.status || 'draft')}
                </td>
                <td className="px-2 py-1.5 text-xs text-muted-foreground max-w-[200px] truncate">
                  {row.worker_ack_notes || (row.status === 'sent' ? 'awaiting worker' : '—')}
                </td>
                <td className="px-2 py-1.5 text-right">
                  <div className="flex justify-end gap-1">
                    {row.status === 'draft' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => sendRow(row)}
                        disabled={busyRow === row.id}
                        data-testid={`send-row-${row.id}`}
                      >
                        {busyRow === row.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                      </Button>
                    )}
                    {row.status === 'sent' && row._kind === 'allocation' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => reSendRow(row)}
                        disabled={busyRow === row.id}
                        data-testid={`resend-row-${row.id}`}
                        title="Re-send notification"
                      >
                        {busyRow === row.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                      </Button>
                    )}
                    {(row.status === 'sent' || row.requires_re_ack) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => adminConfirmRow(row)}
                        disabled={busyRow === row.id}
                        data-testid={`admin-confirm-row-${row.id}`}
                        title="Mark as confirmed (admin override)"
                      >
                        {busyRow === row.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    <ConfirmationDialog
      open={confirmDialog.open}
      onOpenChange={(open) => setConfirmDialog(d => ({ ...d, open }))}
      title={confirmDialog.title}
      description={confirmDialog.description}
      onConfirm={confirmDialog.onConfirm}
      confirmText="Confirm"
    />
    </>
  );
};

export default JobAckPanel;
